import { computed, onMounted, type Ref, ref, watch } from "vue";

import { when } from "./wait";

const DEFAULT_MODAL_HEIGHT = 480;
const LEFTBAR_WIDTH = 238;
const SWIPE_THRESHOLD = 0.5;
const MIN_SWIPE_DISTANCE = 30;
const TOUCH_THRESHOLD = 80;

export const useMobileSheetSwipe = (closeSheet: () => void) => {
  const sheetState = ref<"normal" | "full" | "closing">("normal");
  const isDraggingSheet = ref(false);
  const isAnimating = ref(false);
  const startY = ref(0);
  const currentY = ref(0);
  const sheetHeight = ref(0);
  const viewportHeight = ref(window.innerHeight);

  const canExpandToFullHeight = computed(() => sheetHeight.value >= DEFAULT_MODAL_HEIGHT);
  const fullSheetHeight = computed(() => viewportHeight.value * 0.98);

  const close = async () => {
    sheetState.value = "closing";
    isAnimating.value = true;
    const animationDone = ref(false);

    const start = performance.now();
    const duration = 600;
    const step = (now: number) => {
      if (now - start >= duration) {
        animationDone.value = true;
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);

    closeSheet();

    await when(animationDone);
    isAnimating.value = false;
    sheetState.value = "normal";
  };

  const onTouchstart = (event: TouchEvent) => {
    if (isDraggingSheet.value || isAnimating.value) {
      return;
    }
    const sheetElement = event.currentTarget as HTMLElement;
    if (!sheetElement) {
      return;
    }
    const rect = sheetElement.getBoundingClientRect();
    sheetHeight.value = rect.height;
    const touchY = event.touches[0].clientY;
    if (touchY - rect.top <= TOUCH_THRESHOLD) {
      isDraggingSheet.value = true;
      startY.value = touchY;
      currentY.value = touchY;
    }
  };

  const onTouchmove = (event: TouchEvent) => {
    if (!isDraggingSheet.value || isAnimating.value) {
      return;
    }
    currentY.value = event.touches[0].clientY;
    const deltaY = currentY.value - startY.value;
    if (Math.abs(deltaY) > MIN_SWIPE_DISTANCE) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onTouchend = () => {
    if (!isDraggingSheet.value || isAnimating.value) {
      return;
    }
    const deltaY = currentY.value - startY.value;
    const actualHeight = sheetState.value === "full" ? fullSheetHeight.value : sheetHeight.value;
    const swipeRatio = deltaY / actualHeight;

    if (sheetState.value === "normal") {
      if (deltaY < 0 && canExpandToFullHeight.value) {
        sheetState.value = "full";
      } else if (deltaY > 0 && swipeRatio > SWIPE_THRESHOLD) {
        close();
      }
    } else if (sheetState.value === "full") {
      if (deltaY > 0) {
        if (swipeRatio > SWIPE_THRESHOLD) {
          close();
        } else if (deltaY > MIN_SWIPE_DISTANCE) {
          sheetState.value = "normal";
        }
      }
    }

    isDraggingSheet.value = false;
    startY.value = 0;
    currentY.value = 0;
  };

  const sheetStyles = computed(() => {
    const deltaY = isDraggingSheet.value ? currentY.value - startY.value : 0;
    const isNormal = sheetState.value === "normal";
    const baseStyles = {
      bottom: "0",
      maxHeight: "98vh",
      transition: isDraggingSheet.value ? "none" : "transform 0.3s ease-out",
    };

    if (isDraggingSheet.value) {
      if (isNormal) {
        const newHeight =
          deltaY < 0 && canExpandToFullHeight.value
            ? Math.min(DEFAULT_MODAL_HEIGHT - deltaY, fullSheetHeight.value)
            : DEFAULT_MODAL_HEIGHT;
        return {
          ...baseStyles,
          transform: deltaY > 0 ? `translateY(${deltaY}px)` : undefined,
          height: deltaY < 0 && canExpandToFullHeight.value ? `${newHeight}px` : undefined,
          maxHeight: `${newHeight}px`,
        };
      }
      const adjustedHeight = fullSheetHeight.value;
      const translateY = deltaY > 0 ? Math.max(0, deltaY - (fullSheetHeight.value - DEFAULT_MODAL_HEIGHT)) : 0;
      return {
        ...baseStyles,
        height:
          deltaY > 0 ? `${Math.max(DEFAULT_MODAL_HEIGHT, fullSheetHeight.value - deltaY)}px` : `${adjustedHeight}px`,
        maxHeight:
          deltaY > 0 ? `${Math.max(DEFAULT_MODAL_HEIGHT, fullSheetHeight.value - deltaY)}px` : `${adjustedHeight}px`,
        transform: translateY > 0 ? `translateY(${translateY}px)` : undefined,
      };
    }

    if (sheetState.value === "closing" || isAnimating.value) {
      return {
        ...baseStyles,
        transform: "translateY(100%)",
        transition: "transform 0.3s ease-out",
        height: sheetState.value === "full" ? `${fullSheetHeight.value}px` : undefined,
        maxHeight: sheetState.value === "full" ? `${fullSheetHeight.value}px` : `${DEFAULT_MODAL_HEIGHT}px`,
      };
    }

    return {
      ...baseStyles,
      height: sheetState.value === "full" ? `${fullSheetHeight.value}px` : undefined,
      maxHeight: sheetState.value === "full" ? undefined : `${DEFAULT_MODAL_HEIGHT}px`,
    };
  });

  onMounted(() => {
    viewportHeight.value = window.innerHeight;
    window.addEventListener("resize", () => {
      viewportHeight.value = window.innerHeight;
    });
  });

  return {
    sheetStyles,
    onTouchstart,
    onTouchmove,
    onTouchend,
  };
};

export const useLeftbarSwipe = (
  isLeftbarOpen: Ref<boolean>,
  isAnyModalOpen: Ref<boolean>,
  setMobileLeftbarOpen: (isOpen: boolean) => void
) => {
  const swipeProgress = ref(isLeftbarOpen.value ? 1 : 0);
  const isSwiping = ref(false);
  const startX = ref(0);
  const currentX = ref(0);
  const touchId = ref<number | null>(null);
  const swipeGesture = ref<"edge" | "close" | null>(null);
  const isAnimating = ref(false);
  const hasMoved = ref(false);
  const animationDone = ref(false);

  const startAnimation = () => {
    animationDone.value = false;
    isAnimating.value = true;

    const start = performance.now();
    const currentPosition = (swipeProgress.value - 1) * LEFTBAR_WIDTH;
    const targetPosition = isLeftbarOpen.value ? 0 : -LEFTBAR_WIDTH;
    const distanceToTravel = Math.abs(targetPosition - currentPosition);
    const duration = distanceToTravel;

    const step = (now: number) => {
      if (now - start >= duration) {
        animationDone.value = true;
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const findTouch = (touches: TouchList) => Array.from(touches).find((t) => t.identifier === touchId.value) || null;

  const calculateProgress = () => {
    const deltaX = currentX.value - startX.value;
    if (swipeGesture.value === "edge") {
      return Math.max(0, Math.min(1, deltaX / LEFTBAR_WIDTH));
    }
    if (swipeGesture.value === "close") {
      return Math.max(0, Math.min(1, 1 - Math.max(0, -deltaX) / LEFTBAR_WIDTH));
    }
    return 0;
  };

  const resetSwipeState = () => {
    isSwiping.value = false;
    touchId.value = null;
    swipeGesture.value = null;
    hasMoved.value = false;
  };

  const onTouchstart = (e: TouchEvent) => {
    if (isAnimating.value || isSwiping.value || isAnyModalOpen.value) {
      return;
    }

    const touch = e.touches[0];
    const x = touch.clientX;

    if (isLeftbarOpen.value && x > LEFTBAR_WIDTH) {
      setMobileLeftbarOpen(false);
      return;
    }

    if (isLeftbarOpen.value ? x <= LEFTBAR_WIDTH : x <= 20) {
      isSwiping.value = true;
      swipeGesture.value = isLeftbarOpen.value ? "close" : "edge";
      swipeProgress.value = isLeftbarOpen.value ? 1 : 0;
      startX.value = x;
      currentX.value = x;
      touchId.value = touch.identifier;
    }
  };

  const onTouchmove = (e: TouchEvent) => {
    const activeTouch = findTouch(e.touches);
    if (!activeTouch || !isSwiping.value) {
      return;
    }

    currentX.value = activeTouch.clientX;
    const deltaX = currentX.value - startX.value;

    if (Math.abs(deltaX) > MIN_SWIPE_DISTANCE) {
      hasMoved.value = true;
      e.preventDefault();
      if (swipeGesture.value === "edge" && deltaX > 0) {
        setMobileLeftbarOpen(true);
      }
    }

    swipeProgress.value = calculateProgress();
  };

  const onTouchend = async () => {
    if (!isSwiping.value || !hasMoved.value) {
      resetSwipeState();
      return;
    }

    const shouldOpen = swipeProgress.value >= SWIPE_THRESHOLD;
    resetSwipeState();
    startAnimation();
    setMobileLeftbarOpen(shouldOpen);
    swipeProgress.value = shouldOpen ? 1 : 0;

    await when(animationDone);
    isAnimating.value = false;
  };

  const swipingStyles = computed(() =>
    swipeProgress.value === 0 && !isSwiping.value && !isAnimating.value
      ? undefined
      : `transform: translateX(${(swipeProgress.value - 1) * LEFTBAR_WIDTH}px); transition: ${
          isSwiping.value ? "none" : "transform 0.3s ease-out"
        };`
  );

  watch(
    () => isLeftbarOpen.value,
    (newValue) => {
      if (isSwiping.value) {
        return;
      }
      swipeProgress.value = newValue ? 1 : 0;
    }
  );

  return { swipingStyles, onTouchstart, onTouchmove, onTouchend };
};
